import styled from '@emotion/styled';
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Link } from 'react-router-dom';

import type { FreeTextbooksSyllabusSelectorTextbookSelectorQuery } from '__free_textbooks_generated__/FreeTextbooksSyllabusSelectorTextbookSelectorQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import ChevronRight from 'ms-components/icons/ChevronRight';
import PageDescription from 'ms-helpers/PageDescription';
import PageTitle from 'ms-helpers/PageTitle';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import { setLastSelectedFreeTextbook } from 'ms-pages/FreeTextbooks/FreeTextbooksRedirector';
import type { CountryCode } from 'ms-pages/FreeTextbooks/types';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { font, fontFamily, fontSize, fontWeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import { multilineTextOverflow, tappable } from 'ms-utils/emotion';
import _ from 'ms-utils/relay/extractNode';

import {
  Subtitle as SyllabusSubtitle,
  SyllabusDetails,
  SyllabusGridWrapper,
  SyllabusItem,
  SyllabusThumbnail,
  ThumbnailWrapper,
  Title as SyllabusTitle,
} from '../FreeTextbooksSyllabusSelector/elements';
import seo from '../seo';
import {
  getCurriculumSelectorUrl,
  getFreeTextbooksEmptyViewUrl,
} from '../urls';

const Title = styled.h1({
  color: colors.cloudBurst,
  ...multilineTextOverflow(2),
  ...font.gilroyLargerHeading,
  ...tappable,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 32,
  paddingBottom: 8,
});

const SubTitle = styled(Link)({
  display: 'block',
  fontFamily: fontFamily.body,
  fontSize: fontSize.medium,
  fontWeight: fontWeight.semibold,
  color: colors.eggplant,
  paddingBottom: 20,
  textDecoration: 'none',
});

const FREE_TEXTBOOKS_SYLLABUS_SELECTOR_TEXTBOOK_SELECTOR_QUERY = graphql`
  query FreeTextbooksSyllabusSelectorTextbookSelectorQuery($curriculumId: ID!) {
    curriculum(id: $curriculumId) {
      title
      syllabuses {
        edges {
          node {
            id
            title
            thumbnailImageUrl
            topics {
              totalCount
            }
          }
        }
      }
    }
  }
`;

type Props = { curriculumId: string; countryCode: CountryCode };

export default function FreeTextbooksSyllabusSelectorTextbookSelector({
  curriculumId,
  countryCode,
}: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSyllabusSelectorTextbookSelector">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeTextbooksSyllabusSelectorTextbookSelectorInner
            curriculumId={curriculumId}
            countryCode={countryCode}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSyllabusSelectorTextbookSelectorInner({
  curriculumId,
  countryCode,
  fetchKey,
}: Props & { fetchKey: string }) {
  const data =
    useLazyLoadQuery<FreeTextbooksSyllabusSelectorTextbookSelectorQuery>(
      FREE_TEXTBOOKS_SYLLABUS_SELECTOR_TEXTBOOK_SELECTOR_QUERY,
      {
        curriculumId,
      },
      {
        fetchKey,
        networkCacheConfig: {
          // @ts-expect-error TODO fix this once we upgrade relay and the types are sound
          useCache: true,
          ttl: Infinity,
        },
      },
    );

  const { trackStructEvent } = useSnowplow();

  const { curriculum } = data;
  if (curriculum == null) return <>Curriculum not found</>;
  const syllabuses = _(curriculum.syllabuses);
  return (
    <>
      <PageTitle subtitles={seo.TEXTBOOK_SELECTOR.title(curriculum.title)} />
      <PageDescription
        description={seo.TEXTBOOK_SELECTOR.description(curriculum.title)}
      />

      <Title>Select a Textbook - {curriculum.title}</Title>
      <SubTitle to={getCurriculumSelectorUrl({ countryCode })}>
        Browse other curriculums
      </SubTitle>
      <SyllabusGridWrapper>
        {syllabuses.map(syllabus => (
          <SyllabusItem
            key={syllabus.id}
            to={getFreeTextbooksEmptyViewUrl({ syllabusId: syllabus.id })}
            onClick={() => {
              trackStructEvent({
                category: 'free_textbook',
                action: 'select_textbook',
                property: 'textbookId',
                label: syllabus.id,
              });
              setLastSelectedFreeTextbook(syllabus.id);
            }}
          >
            <ThumbnailWrapper>
              <SyllabusThumbnail src={syllabus.thumbnailImageUrl} />
            </ThumbnailWrapper>
            <SyllabusDetails>
              <SyllabusTitle oneLine={false}>{syllabus.title}</SyllabusTitle>
              <SyllabusSubtitle>
                {syllabus.topics.totalCount} topics
              </SyllabusSubtitle>
            </SyllabusDetails>
            <ChevronRight size={4 * BASE_UNIT} />
          </SyllabusItem>
        ))}
      </SyllabusGridWrapper>
    </>
  );
}
